import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ObjectTreeSelector from "@/components/ObjectTreeSelector";
import RetentionFields from "@/components/RetentionFields";
import { ArrowLeft } from "lucide-react";

const PRESETS = {
  manual: "", hourly: "0 * * * *", daily: "0 2 * * *",
  weekly: "0 3 * * 0", monthly: "0 4 1 * *",
};

const DEFAULT = {
  name: "", source_id: "", destination_ids: [],
  scope: { mode: "all_accessible", include_system: false, databases: {}, dump_mode: "schema_data", include_routines: true, include_triggers: true, include_events: true },
  schedule_cron: "", timezone: "UTC", enabled: true, compression_level: 6,
  encryption: { enabled: false, method: "age", public_key: "" },
  notifications: { on_success: false, on_failure: true, on_partial: true, email_to: [], webhook_url: "" },
  retention: { mode: "keep_last_n", keep_last_n: 7, keep_days: 30, gfs_hourly: 24, gfs_daily: 7, gfs_weekly: 4, gfs_monthly: 12 },
  single_transaction: true, lock_tables_fallback: false, extra_dump_options: [], timeout_seconds: 7200,
  anomaly: { enabled: true, size_pct: 40, duration_factor: 2.0 },
  verify: { enabled: false, schedule_cron: "0 5 * * 0", target_source_id: "", target_database: "restore_verify", validation_queries: [] },
  binlog: { archive: false, schedule_cron: "0 */6 * * *" },
  engine: "logical", execution_mode: "streaming", backup_type: "full",
  chain_policy: "always_full", chain_full_weekday: 6, incremental_fallback: "full",
  prepare_mode: "none", extra_backup_options: [], physical_partial: false, physical_databases: [],
};

export default function PlanForm() {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT);
  const [sources, setSources] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [report, setReport] = useState(null);
  const [preset, setPreset] = useState("manual");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/sources").then((r) => setSources(r.data));
    api.get("/destinations").then((r) => setDestinations(r.data));
    if (id) {
      api.get(`/plans/${id}`).then((r) => {
        setForm({ ...DEFAULT, ...r.data });
        const found = Object.entries(PRESETS).find(([, c]) => c === r.data.schedule_cron);
        setPreset(found ? found[0] : "custom");
      });
    }
  }, [id]);

  const loadScan = useCallback((sourceId) => {
    if (!sourceId) { setReport(null); return; }
    api.get(`/sources/${sourceId}/scan`).then((r) => setReport(r.data.report)).catch(() => setReport(null));
  }, []);
  useEffect(() => { loadScan(form.source_id); }, [form.source_id, loadScan]);

  const toggleDest = (did) => {
    set("destination_ids", form.destination_ids.includes(did)
      ? form.destination_ids.filter((x) => x !== did)
      : [...form.destination_ids, did]);
  };

  const selectedSource = sources.find((s) => s.id === form.source_id);
  const sshSource = selectedSource?.source_mode === "ssh_mariadb_host";
  const isPhysical = form.engine === "physical";

  const onSourceChange = (v) => {
    const src = sources.find((s) => s.id === v);
    setForm((f) => ({ ...f, source_id: v,
      engine: src?.source_mode === "ssh_mariadb_host" ? "physical" : "logical" }));
  };

  const applyPreset = (p) => { setPreset(p); if (p !== "custom") set("schedule_cron", PRESETS[p]); };

  const save = async () => {
    if (!form.name || !form.source_id || form.destination_ids.length === 0) {
      toast.error(t("planForm.required")); return;
    }
    if (form.encryption.enabled && !form.encryption.public_key) {
      toast.error(t("planForm.encNoKey")); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (preset === "manual") payload.schedule_cron = null;
      if (id) { await api.put(`/plans/${id}`, payload); toast.success(t("planForm.updated")); }
      else { await api.post("/plans", payload); toast.success(t("planForm.created")); }
      navigate("/plans");
    } catch (e) { toast.error(e?.response?.data?.detail || t("planForm.saveFailed")); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="plan-form-page">
      <PageHeader title={id ? t("planForm.editTitle") : t("planForm.newTitle")}
        actions={
          <>
            <Button variant="outline" className="rounded-none" onClick={() => navigate("/plans")}><ArrowLeft className="h-4 w-4 mr-1" />{t("common.back")}</Button>
            <Button className="rounded-none uppercase bg-[#0A0A0A] hover:bg-[#0052FF]" onClick={save} disabled={saving} data-testid="save-plan-button">{saving ? t("common.saving") : t("planForm.savePlan")}</Button>
          </>
        } />
      <div className="p-6 md:p-8 max-w-4xl space-y-6">
        <div className="grid-panel p-5 space-y-4">
          <Field label={t("planForm.planName")}><Input data-testid="plan-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none" /></Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("planForm.source")}>
              <Select value={form.source_id} onValueChange={onSourceChange}>
                <SelectTrigger className="rounded-none" data-testid="plan-source"><SelectValue placeholder={t("planForm.selectSource")} /></SelectTrigger>
                <SelectContent>{sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.source_mode === "ssh_mariadb_host" ? "⬛ " : ""}{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={t("planForm.destinations")}>
              <div className="border border-input p-2 space-y-1.5 max-h-28 overflow-y-auto" data-testid="plan-destinations">
                {destinations.length === 0 && <p className="text-xs text-muted-foreground">{t("planForm.noDest")}</p>}
                {destinations.map((d) => (
                  <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.destination_ids.includes(d.id)} onCheckedChange={() => toggleDest(d.id)} data-testid={`plan-dest-${d.id}`} />
                    {d.name} <span className="font-mono text-[10px] text-muted-foreground uppercase">{d.type}</span>
                  </label>
                ))}
              </div>
            </Field>
          </div>
        </div>

        {sshSource && (
          <div className="grid-panel p-5 space-y-4" data-testid="engine-panel">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold">{t("planForm.backupEngine")}</h3>
              <span className="font-mono text-[10px] uppercase text-brand">{t("planForm.sshSource")}</span>
            </div>
            <Field label={t("planForm.engine")}>
              <div className="grid grid-cols-2 gap-px bg-border border border-border">
                {[["physical", t("planForm.enginePhysical"), t("planForm.enginePhysicalSub")],
                  ["logical", t("planForm.engineLogical"), t("planForm.engineLogicalSub")]].map(([v, tt, sub]) => (
                  <button key={v} type="button" data-testid={`engine-${v}`} onClick={() => set("engine", v)}
                    className={`px-3 py-2.5 text-left transition-colors ${form.engine === v ? "bg-[#0A0A0A] text-white" : "bg-card hover:bg-secondary"}`}>
                    <span className="block text-sm font-semibold">{tt}</span>
                    <span className={`block font-mono text-[10px] ${form.engine === v ? "text-white/60" : "text-muted-foreground"}`}>{sub}</span>
                  </button>
                ))}
              </div>
            </Field>
            {isPhysical && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t("planForm.executionMode")}>
                    <Select value={form.execution_mode} onValueChange={(v) => set("execution_mode", v)}>
                      <SelectTrigger className="rounded-none" data-testid="execution-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="streaming">{t("planForm.execStreaming")}</SelectItem>
                        <SelectItem value="remote_staging">{t("planForm.execStaging")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t("planForm.chainPolicy")}>
                    <Select value={form.chain_policy} onValueChange={(v) => set("chain_policy", v)}>
                      <SelectTrigger className="rounded-none" data-testid="chain-policy"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="always_full">{t("planForm.chainAlwaysFull")}</SelectItem>
                        <SelectItem value="weekly_full_daily_incr">{t("planForm.chainWeekly")}</SelectItem>
                        <SelectItem value="custom">{t("planForm.chainCustom")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {form.chain_policy === "custom" && (
                    <Field label={t("planForm.backupType")}>
                      <Select value={form.backup_type} onValueChange={(v) => set("backup_type", v)}>
                        <SelectTrigger className="rounded-none" data-testid="backup-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">{t("planForm.full")}</SelectItem>
                          <SelectItem value="incremental">{t("planForm.incremental")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <Field label={t("planForm.prepareMode")}>
                    <Select value={form.prepare_mode} onValueChange={(v) => set("prepare_mode", v)}>
                      <SelectTrigger className="rounded-none" data-testid="prepare-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("planForm.prepNone")}</SelectItem>
                        <SelectItem value="on_source">{t("planForm.prepOnSource")}</SelectItem>
                        <SelectItem value="restore_only">{t("planForm.prepRestoreOnly")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  {form.chain_policy !== "always_full" && (
                    <Field label={t("planForm.ifNoBase")}>
                      <Select value={form.incremental_fallback} onValueChange={(v) => set("incremental_fallback", v)}>
                        <SelectTrigger className="rounded-none" data-testid="incr-fallback"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full">{t("planForm.fallbackFull")}</SelectItem>
                          <SelectItem value="fail">{t("planForm.fallbackFail")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                </div>
                <p className="font-mono text-[11px] text-yellow-700 border border-yellow-300 bg-yellow-50 p-3">
                  {t("planForm.physicalWarn")}
                </p>
              </>
            )}
          </div>
        )}

        <Tabs defaultValue="scope">
          <TabsList className="rounded-none bg-secondary h-auto flex-wrap justify-start">
            <TabsTrigger value="scope" className="rounded-none" data-testid="tab-scope">{t("planForm.tabScope")}</TabsTrigger>
            <TabsTrigger value="schedule" className="rounded-none" data-testid="tab-schedule">{t("planForm.tabSchedule")}</TabsTrigger>
            <TabsTrigger value="options" className="rounded-none" data-testid="tab-options">{t("planForm.tabOptions")}</TabsTrigger>
            <TabsTrigger value="security" className="rounded-none" data-testid="tab-security">{t("planForm.tabSecurity")}</TabsTrigger>
            <TabsTrigger value="verify" className="rounded-none" data-testid="tab-verify">{t("planForm.tabVerify")}</TabsTrigger>
            <TabsTrigger value="notify" className="rounded-none" data-testid="tab-notify">{t("planForm.tabNotify")}</TabsTrigger>
          </TabsList>

          <TabsContent value="scope" className="space-y-4 mt-4">
            {isPhysical ? (
              <div className="grid-panel p-5 space-y-3" data-testid="physical-scope-notice">
                <h3 className="font-display font-bold">{t("planForm.physTitle")}</h3>
                <p className="text-sm text-muted-foreground">{t("planForm.physBody")}</p>
              </div>
            ) : (
            <>
            <div className="grid-panel p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("planForm.backupAll")}</Label>
                  <p className="text-xs text-muted-foreground">{t("planForm.backupAllSub")}</p>
                </div>
                <Switch checked={form.scope.mode === "all_accessible"}
                  onCheckedChange={(c) => set("scope", { ...form.scope, mode: c ? "all_accessible" : "custom" })}
                  data-testid="scope-all-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("planForm.includeSystem")}</Label>
                  <p className="text-xs text-yellow-700">{t("planForm.includeSystemWarn")}</p>
                </div>
                <Switch checked={form.scope.include_system} onCheckedChange={(c) => set("scope", { ...form.scope, include_system: c })} data-testid="scope-system-toggle" />
              </div>
              <Field label={t("planForm.dumpMode")}>
                <Select value={form.scope.dump_mode} onValueChange={(v) => set("scope", { ...form.scope, dump_mode: v })}>
                  <SelectTrigger className="rounded-none" data-testid="scope-dump-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="schema_data">{t("planForm.schemaData")}</SelectItem>
                    <SelectItem value="schema_only">{t("planForm.schemaOnly")}</SelectItem>
                    <SelectItem value="data_only">{t("planForm.dataOnly")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("planForm.includeRoutines")}</Label>
                  <p className="text-xs text-muted-foreground">{t("planForm.includeRoutinesSub")}</p>
                </div>
                <Switch checked={form.scope.include_routines !== false}
                  onCheckedChange={(c) => set("scope", { ...form.scope, include_routines: c })}
                  data-testid="scope-routines-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <div><Label>{t("planForm.includeTriggers")}</Label></div>
                <Switch checked={form.scope.include_triggers !== false}
                  onCheckedChange={(c) => set("scope", { ...form.scope, include_triggers: c })}
                  data-testid="scope-triggers-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <div><Label>{t("planForm.includeEvents")}</Label></div>
                <Switch checked={form.scope.include_events !== false}
                  onCheckedChange={(c) => set("scope", { ...form.scope, include_events: c })}
                  data-testid="scope-events-toggle" />
              </div>
            </div>
            {form.scope.mode === "custom" && (
              report ? <ObjectTreeSelector report={report} scope={form.scope} onChange={(s) => set("scope", s)} />
                : <div className="grid-panel p-8 text-center text-sm text-muted-foreground">{t("planForm.selectSourceScan")}</div>
            )}
            </>
            )}
          </TabsContent>

          <TabsContent value="schedule" className="mt-4">
            <div className="grid-panel p-5 space-y-4">
              <Field label={t("planForm.frequency")}>
                <Select value={preset} onValueChange={applyPreset}>
                  <SelectTrigger className="rounded-none" data-testid="schedule-preset"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">{t("planForm.freqManual")}</SelectItem>
                    <SelectItem value="hourly">{t("planForm.freqHourly")}</SelectItem>
                    <SelectItem value="daily">{t("planForm.freqDaily")}</SelectItem>
                    <SelectItem value="weekly">{t("planForm.freqWeekly")}</SelectItem>
                    <SelectItem value="monthly">{t("planForm.freqMonthly")}</SelectItem>
                    <SelectItem value="custom">{t("planForm.freqCustom")}</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {preset !== "manual" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t("planForm.cronExpr")}><Input value={form.schedule_cron} onChange={(e) => set("schedule_cron", e.target.value)} className="rounded-none font-mono" data-testid="schedule-cron" disabled={preset !== "custom"} /></Field>
                  <Field label={t("planForm.timezone")}><Input value={form.timezone} onChange={(e) => set("timezone", e.target.value)} className="rounded-none" data-testid="schedule-tz" placeholder="UTC" /></Field>
                </div>
              )}
              <div className="flex items-center justify-between"><Label>{t("planForm.planEnabled")}</Label><Switch checked={form.enabled} onCheckedChange={(c) => set("enabled", c)} data-testid="plan-enabled" /></div>
            </div>
          </TabsContent>

          <TabsContent value="options" className="mt-4">
            <div className="grid-panel p-5 space-y-4">
              <Field label={t("planForm.compressionLevel", { level: form.compression_level })}>
                <input type="range" min="1" max="19" value={form.compression_level} onChange={(e) => set("compression_level", Number(e.target.value))} className="w-full" data-testid="compression-level" />
              </Field>
              {!isPhysical ? (<>
              <div className="flex items-center justify-between">
                <div><Label>--single-transaction</Label><p className="text-xs text-muted-foreground">{t("planForm.singleTxnSub")}</p></div>
                <Switch checked={form.single_transaction} onCheckedChange={(c) => set("single_transaction", c)} data-testid="single-transaction" />
              </div>
              <div className="flex items-center justify-between">
                <div><Label>{t("planForm.lockTables")}</Label><p className="text-xs text-yellow-700">{t("planForm.lockTablesSub")}</p></div>
                <Switch checked={form.lock_tables_fallback} onCheckedChange={(c) => set("lock_tables_fallback", c)} data-testid="lock-tables" />
              </div>
              <Field label={t("planForm.extraDump")}>
                <Input value={form.extra_dump_options.join(",")} onChange={(e) => set("extra_dump_options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="rounded-none font-mono" data-testid="extra-options" placeholder="--no-tablespaces" />
              </Field>
              </>) : (
              <Field label={t("planForm.extraBackup")}>
                <Input value={form.extra_backup_options.join(",")} onChange={(e) => set("extra_backup_options", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} className="rounded-none font-mono" data-testid="extra-backup-options" placeholder="--galera-info" />
              </Field>
              )}
              <Field label={t("planForm.timeout")}><Input type="number" value={form.timeout_seconds} onChange={(e) => set("timeout_seconds", Number(e.target.value))} className="rounded-none" data-testid="timeout" /></Field>
            </div>
          </TabsContent>

          <TabsContent value="security" className="mt-4 space-y-4">
            <div className="grid-panel p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div><Label>{t("planForm.encrypt")}</Label><p className="text-xs text-yellow-700">{t("planForm.encryptSub")}</p></div>
                <Switch checked={form.encryption.enabled} onCheckedChange={(c) => set("encryption", { ...form.encryption, enabled: c })} data-testid="encryption-enabled" />
              </div>
              {form.encryption.enabled && (<>
                <Field label={t("planForm.method")}>
                  <Select value={form.encryption.method} onValueChange={(v) => set("encryption", { ...form.encryption, method: v })}>
                    <SelectTrigger className="rounded-none" data-testid="encryption-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="age">{t("planForm.methodAge")}</SelectItem>
                      <SelectItem value="gpg">GPG</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={form.encryption.method === "gpg" ? t("planForm.gpgKey") : t("planForm.ageKey")}>
                  {form.encryption.method === "gpg"
                    ? <textarea value={form.encryption.public_key} onChange={(e) => set("encryption", { ...form.encryption, public_key: e.target.value })} rows={3} className="w-full rounded-none border border-input p-2 font-mono text-xs" data-testid="encryption-pubkey" placeholder="-----BEGIN PGP PUBLIC KEY BLOCK-----" />
                    : <Input value={form.encryption.public_key} onChange={(e) => set("encryption", { ...form.encryption, public_key: e.target.value })} className="rounded-none font-mono" data-testid="encryption-pubkey" placeholder="age1..." />}
                </Field>
              </>)}
            </div>
            <RetentionFields value={form.retention} onChange={(r) => set("retention", r)} />
          </TabsContent>

          <TabsContent value="verify" className="mt-4 space-y-4">
            <div className="grid-panel p-5 space-y-4">
              <h3 className="font-display font-bold">{t("planForm.autoVerifyTitle")}</h3>
              <p className="text-xs text-muted-foreground">{t("planForm.autoVerifyBody")}</p>
              <div className="flex items-center justify-between">
                <Label>{t("planForm.enableScheduledVerify")}</Label>
                <Switch checked={form.verify.enabled} onCheckedChange={(c) => set("verify", { ...form.verify, enabled: c })} data-testid="verify-enabled" />
              </div>
              {form.verify.enabled && (<>
                <Field label={t("planForm.verifySchedule")}><Input value={form.verify.schedule_cron} onChange={(e) => set("verify", { ...form.verify, schedule_cron: e.target.value })} className="rounded-none font-mono" data-testid="verify-cron" /></Field>
                <Field label={t("planForm.restoreTarget")}>
                  <Select value={form.verify.target_source_id} onValueChange={(v) => set("verify", { ...form.verify, target_source_id: v })}>
                    <SelectTrigger className="rounded-none" data-testid="verify-target"><SelectValue placeholder={t("planForm.chooseTarget")} /></SelectTrigger>
                    <SelectContent>{sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t("planForm.targetDb")}><Input value={form.verify.target_database} onChange={(e) => set("verify", { ...form.verify, target_database: e.target.value })} className="rounded-none" data-testid="verify-db" /></Field>
                <Field label={t("planForm.validationQueries")}>
                  <textarea value={(form.verify.validation_queries || []).join("\n")} onChange={(e) => set("verify", { ...form.verify, validation_queries: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} rows={2} className="w-full rounded-none border border-input p-2 font-mono text-xs" data-testid="verify-queries" />
                </Field>
              </>)}
            </div>

            <div className="grid-panel p-5 space-y-4">
              <h3 className="font-display font-bold">{t("planForm.anomalyTitle")}</h3>
              <div className="flex items-center justify-between">
                <Label>{t("planForm.anomalyToggle")}</Label>
                <Switch checked={form.anomaly.enabled} onCheckedChange={(c) => set("anomaly", { ...form.anomaly, enabled: c })} data-testid="anomaly-enabled" />
              </div>
              {form.anomaly.enabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t("planForm.sizeDev", { pct: form.anomaly.size_pct })}>
                    <input type="range" min="10" max="90" value={form.anomaly.size_pct} onChange={(e) => set("anomaly", { ...form.anomaly, size_pct: Number(e.target.value) })} className="w-full" data-testid="anomaly-size" />
                  </Field>
                  <Field label={t("planForm.durationFactor", { factor: form.anomaly.duration_factor })}>
                    <input type="range" min="1.5" max="5" step="0.5" value={form.anomaly.duration_factor} onChange={(e) => set("anomaly", { ...form.anomaly, duration_factor: Number(e.target.value) })} className="w-full" data-testid="anomaly-duration" />
                  </Field>
                </div>
              )}
            </div>

            <div className="grid-panel p-5 space-y-4">
              <h3 className="font-display font-bold">{t("planForm.binlogTitle")}</h3>
              <p className="text-xs text-yellow-700">{t("planForm.binlogBody")}</p>
              <div className="flex items-center justify-between">
                <Label>{t("planForm.enableBinlog")}</Label>
                <Switch checked={form.binlog.archive} onCheckedChange={(c) => set("binlog", { ...form.binlog, archive: c })} data-testid="binlog-enabled" />
              </div>
              {form.binlog.archive && (
                <Field label={t("planForm.binlogSchedule")}><Input value={form.binlog.schedule_cron} onChange={(e) => set("binlog", { ...form.binlog, schedule_cron: e.target.value })} className="rounded-none font-mono" data-testid="binlog-cron" /></Field>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notify" className="mt-4">
            <div className="grid-panel p-5 space-y-4">
              <div className="flex items-center justify-between"><Label>{t("planForm.notifySuccess")}</Label><Switch checked={form.notifications.on_success} onCheckedChange={(c) => set("notifications", { ...form.notifications, on_success: c })} data-testid="notify-success" /></div>
              <div className="flex items-center justify-between"><Label>{t("planForm.notifyFailure")}</Label><Switch checked={form.notifications.on_failure} onCheckedChange={(c) => set("notifications", { ...form.notifications, on_failure: c })} data-testid="notify-failure" /></div>
              <div className="flex items-center justify-between"><Label>{t("planForm.notifyPartial")}</Label><Switch checked={form.notifications.on_partial} onCheckedChange={(c) => set("notifications", { ...form.notifications, on_partial: c })} data-testid="notify-partial" /></div>
              <Field label={t("planForm.emailRecipients")}>
                <Input value={form.notifications.email_to.join(",")} onChange={(e) => set("notifications", { ...form.notifications, email_to: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} className="rounded-none" data-testid="notify-email" />
              </Field>
              <Field label={t("planForm.webhookUrl")}>
                <Input value={form.notifications.webhook_url || ""} onChange={(e) => set("notifications", { ...form.notifications, webhook_url: e.target.value })} className="rounded-none font-mono" data-testid="notify-webhook" />
              </Field>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
